import { Overlay, ScrollStrategy } from "@angular/cdk/overlay";
import { ComponentType } from "@angular/cdk/portal";
import { ComponentRef, inject, Injectable, InjectionToken, OnDestroy, TemplateRef, Type } from "@angular/core";
import { defer, Observable, Subject } from "rxjs";
import { startWith } from "rxjs/operators";
import { VdsDialogConfig } from "./dialog-config";
import { VdsDialogContainer } from "./dialog-container/dialog-container";
import { VdsDialogRef } from "./dialog-ref";
import { Dialog, DialogConfig } from "@angular/cdk/dialog";

export const VDS_DIALOG_DATA = new InjectionToken<any>("VdsDialogData");
export const VDS_DIALOG_DEFAULT_OPTIONS = new InjectionToken<VdsDialogConfig>("vds-dialog-default-options");

export const VDS_DIALOG_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>("vds-dialog-scroll-strategy", {
    providedIn: "root",
    factory: () => {
        const overlay = inject(Overlay);
        return () => overlay.scrollStrategies.block();
    }
});

@Injectable({ providedIn: "root" })
export class VdsDialog implements OnDestroy {
    protected static uniqueId = 0;
    private _overlay = inject(Overlay);
    private _defaultOptions = inject<VdsDialogConfig>(VDS_DIALOG_DEFAULT_OPTIONS, { optional: true });
    private _scrollStrategy = inject(VDS_DIALOG_SCROLL_STRATEGY);
    private _parentDialog = inject(VdsDialog, { optional: true, skipSelf: true });
    private _dialog = inject(Dialog);

    private readonly _openDialogsAtThisLevel: VdsDialogRef<any>[] = [];
    private readonly _afterAllClosedAtThisLevel = new Subject<void>();
    private readonly _afterOpenedAtThisLevel = new Subject<VdsDialogRef<any>>();
    protected dialogConfigClass = VdsDialogConfig;

    private readonly _dialogRefConstructor: Type<VdsDialogRef<any>>;
    private readonly _dialogContainerType: Type<VdsDialogContainer>;
    private readonly _dialogDataToken: InjectionToken<any>;

    /** Keeps track of the currently-open dialogs. */
    get openDialogs(): VdsDialogRef<any>[] {
        return this._parentDialog ? this._parentDialog.openDialogs : this._openDialogsAtThisLevel;
    }

    /** Stream that emits when a dialog has been opened. */
    get afterOpened(): Subject<VdsDialogRef<any>> {
        return this._parentDialog ? this._parentDialog.afterOpened : this._afterOpenedAtThisLevel;
    }

    private _getAfterAllClosed(): Subject<void> {
        const parent = this._parentDialog;
        return parent ? parent._getAfterAllClosed() : this._afterAllClosedAtThisLevel;
    }

    readonly afterAllClosed: Observable<void> = defer(() =>
        this.openDialogs.length ? this._getAfterAllClosed() : this._getAfterAllClosed().pipe(startWith(undefined))
    ) as Observable<any>;

    // eslint-disable-next-line @angular-eslint/prefer-inject
    constructor(...args: unknown[]);

    constructor() {
        this._dialogRefConstructor = VdsDialogRef;
        this._dialogContainerType = VdsDialogContainer;
        this._dialogDataToken = VDS_DIALOG_DATA;
    }

    /**
     * Opens a modal dialog containing the given component.
     * @param component Type of the component to load into the dialog.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened dialog.
     */
    open<T, D = any, R = any>(component: ComponentType<T>, config?: VdsDialogConfig<D>): VdsDialogRef<T, R>;

    /**
     * Opens a modal dialog containing the given template.
     * @param template TemplateRef to instantiate as the dialog content.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened dialog.
     */
    open<T, D = any, R = any>(template: TemplateRef<T>, config?: VdsDialogConfig<D>): VdsDialogRef<T, R>;

    open<T, D = any, R = any>(component: ComponentType<T>, config?: VdsDialogConfig<D>): VdsDialogRef<T, R>;

    open<T, D = any, R = any>(template: ComponentType<T> | TemplateRef<T>, config?: VdsDialogConfig<D>): VdsDialogRef<T, R>;

    open<T, D = any, R = any>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, config?: VdsDialogConfig<D>): VdsDialogRef<T, R> {
        let dialogRef: VdsDialogRef<T, R>;
        config = { ...(this._defaultOptions || new VdsDialogConfig()), ...config };
        config.id = config.id || `vds-dialog-${VdsDialog.uniqueId++}`;
        config.scrollStrategy = config.scrollStrategy || this._scrollStrategy();

        const cdkRef = this._dialog.open<R, D, T>(componentOrTemplateRef, {
            ...config,
            positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
            disableClose: true,
            closeOnDestroy: false,
            closeOnOverlayDetachments: false,
            container: {
                type: this._dialogContainerType,
                providers: () => [
                    { provide: this.dialogConfigClass, useValue: config },
                    { provide: DialogConfig, useValue: config }
                ]
            },
            templateContext: () => ({ dialogRef }),
            providers: (ref, cdkConfig, dialogContainer) => {
                dialogRef = new this._dialogRefConstructor(ref, config, dialogContainer);
                dialogRef.updatePosition(config?.position);
                return [
                    { provide: this._dialogContainerType, useValue: dialogContainer },
                    { provide: this._dialogDataToken, useValue: cdkConfig.data },
                    { provide: this._dialogRefConstructor, useValue: dialogRef }
                ];
            }
        });

        // This can't be assigned in the `providers` callback, because
        // the instance hasn't been assigned to the CDK ref yet.
        (dialogRef! as { componentRef: ComponentRef<T> }).componentRef = cdkRef.componentRef!;
        dialogRef!.componentInstance = cdkRef.componentInstance!;

        this.openDialogs.push(dialogRef!);
        this.afterOpened.next(dialogRef!);

        dialogRef!.afterClosed().subscribe(() => {
            const index = this.openDialogs.indexOf(dialogRef);

            if (index > -1) {
                this.openDialogs.splice(index, 1);

                if (!this.openDialogs.length) {
                    this._getAfterAllClosed().next();
                }
            }
        });

        return dialogRef!;
    }

    /**
     * Closes all of the currently-open dialogs.
     */
    closeAll(): void {
        this._closeDialogs(this.openDialogs);
    }

    /**
     * Finds an open dialog by its id.
     * @param id ID to use when looking up the dialog.
     */
    getDialogById(id: string): VdsDialogRef<any> | undefined {
        return this.openDialogs.find((dialog) => dialog.id === id);
    }

    ngOnDestroy() {
        // Only close the dialogs at this level on destroy
        // since the parent service may still be active.
        this._closeDialogs(this._openDialogsAtThisLevel);
        this._afterAllClosedAtThisLevel.complete();
        this._afterOpenedAtThisLevel.complete();
    }

    private _closeDialogs(dialogs: VdsDialogRef<any>[]) {
        let i = dialogs.length;

        while (i--) {
            dialogs[i].close();
        }
    }
}
